/**
 * WHAT A SUPPORT EXECUTIVE MAY DO NEXT — one definition, mirroring the server's.
 *
 * ── WHY THIS EXISTS ────────────────────────────────────────────────────────────────────────
 * The console used to offer a free-form status `<select>` listing all four statuses on the
 * ticket detail page, and a separate set of conditional links in the queue. Neither knew about
 * the other, and neither knew about the lifecycle: the dropdown happily offered
 * `open → resolved`, which the API now refuses with a 400.
 *
 * Leaving that in place and letting the server reject it is not good enough. An action a user
 * can see, click, and be refused is a broken control, not a safe one. So the UI offers only
 * transitions that exist.
 *
 * ── THE SOURCE OF TRUTH IS THE SERVER ──────────────────────────────────────────────────────
 * `apps/api/src/modules/support/support.lifecycle.ts` holds the authoritative table, and it is
 * still enforced on every request. This is the UI's mirror of it, NOT a second authority: if the
 * two ever disagree the server wins and the user sees the error toast plus a refetch (see the
 * `onError` handlers on both pages). The mirror exists so the disagreement is rare, not so the
 * check can be skipped.
 *
 * Deliberately NOT offered anywhere, because the server refuses them:
 *   open   → resolved   a ticket nobody has handled cannot have been solved
 *   closed → resolved   backwards; reopen it first
 *
 * ── RESOLVE IS AN ACT, NOT A SENTENCE ──────────────────────────────────────────────────────
 * `resolved` is reachable from exactly ONE place in this table: the explicit Resolve action on
 * an `in_progress` ticket. Nothing about a message — its text, its length, its tone — can reach
 * it. "We believe this is fixed" is a reply; the ticket moves when someone says it has moved.
 */

export type SupportStatus = 'open' | 'in_progress' | 'resolved' | 'closed';

export type SupportLifecycleActionId = 'start' | 'resolve' | 'close' | 'reopen';

export interface SupportLifecycleAction {
  /** Stable id — drives the label colour each surface already uses. */
  id: SupportLifecycleActionId;
  /** The status this moves the ticket to. Sent as `PATCH { status: to }`. */
  to: SupportStatus;
  /** Human wording. Raw enum tokens are never shown to an operator. */
  label: string;
  /**
   * `primary` is the expected next step in the lifecycle and is the ONE action the dense queue
   * row offers; `secondary` is legitimate but off the main path and appears on the detail page
   * only, where there is room to rank the two.
   */
  kind: 'primary' | 'secondary';
}

/**
 * Per state, in display order.
 *
 * `close` from `open` / `in_progress` is the disposal route for a duplicate, a mistake or spam.
 * It is secondary on purpose: it is a real product capability (there is no `cancelled` status,
 * so this is the only way to discard a ticket) but it is never the expected next step.
 */
export const SUPPORT_LIFECYCLE_ACTIONS: Readonly<
  Record<SupportStatus, readonly SupportLifecycleAction[]>
> = {
  open: [
    { id: 'start', to: 'in_progress', label: 'Start handling', kind: 'primary' },
    { id: 'close', to: 'closed', label: 'Close', kind: 'secondary' },
  ],
  in_progress: [
    { id: 'resolve', to: 'resolved', label: 'Resolve', kind: 'primary' },
    { id: 'close', to: 'closed', label: 'Close', kind: 'secondary' },
  ],
  resolved: [
    { id: 'close', to: 'closed', label: 'Close', kind: 'primary' },
    // Support's fix is a claim until the member confirms it; this is how it comes back.
    { id: 'reopen', to: 'in_progress', label: 'Return to handling', kind: 'secondary' },
  ],
  closed: [
    // A finished case has no forward step, so there is no primary action — only the way back.
    { id: 'reopen', to: 'in_progress', label: 'Reopen', kind: 'secondary' },
  ],
} as const;

/** Every action valid for this status. Unknown status ⇒ no actions, never a guess. */
export function supportLifecycleActions(status: string): readonly SupportLifecycleAction[] {
  return SUPPORT_LIFECYCLE_ACTIONS[status as SupportStatus] ?? [];
}

/**
 * The single action a queue row shows.
 *
 * The queue is a triage surface: one decision per row. Rendering two equal-weight links (which
 * is what a `resolved` row used to do — Close and Reopen side by side, same size, no hierarchy)
 * makes an executive stop and read instead of moving the queue along. The full set is one click
 * away on the ticket itself.
 *
 * Falls back to the first action so `closed`, which has no primary, still offers Reopen.
 */
export function primarySupportAction(status: string): SupportLifecycleAction | null {
  const actions = supportLifecycleActions(status);
  return actions.find((a) => a.kind === 'primary') ?? actions[0] ?? null;
}
